import React, { useState } from 'react';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/AppConfig';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, FormControl, Grid, InputLabel,
  MenuItem, Select, TextField, Typography,
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';

function getAuthHeaders() {
  const token = sessionStorage.getItem('sessionToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const LANGUAGES = [
  { code: 'en', label: 'English', flag: 'EN' },
  { code: 'hi', label: 'Hindi (Hinglish)', flag: 'HI' },
  { code: 'ta', label: 'Tamil', flag: 'TA' },
  { code: 'te', label: 'Telugu', flag: 'TE' },
  { code: 'kn', label: 'Kannada', flag: 'KN' },
  { code: 'mr', label: 'Marathi', flag: 'MR' },
  { code: 'bn', label: 'Bengali', flag: 'BN' },
];

function LanguageTest() {
  const [matchId, setMatchId] = useState('');
  const [message, setMessage] = useState("What's happening in this match?");
  const [language, setLanguage] = useState('hi');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState('');

  const sendMessage = async () => {
    if (!matchId || !message) return;
    setLoading(true);
    setError('');
    setResponse(null);
    try {
      const chatUrl = (BASE_URL || '').replace(/\/api\/?$/, '').replace(':3000', ':3001');
      const res = await fetch(`${chatUrl}/admin/ai-chat/${matchId}/message`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, language }),
      });
      const data = await res.json();
      if (data.data) {
        setResponse(data.data);
      } else {
        setError(data.error || 'No response');
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
        <Typography variant="h5" fontWeight={700} gutterBottom>
          <TranslateIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Multi-Language Chat Test
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Test AI Chat responses in different languages. Same data accuracy, regional voice.
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <TextField label="Match ID" value={matchId} onChange={e => setMatchId(e.target.value)} size="small" fullWidth />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Language</InputLabel>
              <Select value={language} label="Language" onChange={e => setLanguage(e.target.value)}>
                {LANGUAGES.map(l => (
                  <MenuItem key={l.code} value={l.code}>{l.flag} {l.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Message" value={message} onChange={e => setMessage(e.target.value)} size="small" fullWidth />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button variant="contained" onClick={sendMessage} disabled={loading || !matchId} fullWidth sx={{ height: '100%' }}>
              {loading ? <CircularProgress size={20} /> : 'Send'}
            </Button>
          </Grid>
        </Grid>

        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

        {response && (
          <Card elevation={2} sx={{ maxWidth: 700 }}>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Chip label={response.intent} size="small" color="primary" />
                <Chip label={LANGUAGES.find(l => l.code === language)?.label || language} size="small" variant="outlined" />
                <Chip label={response.confidence_score} size="small" color={response.confidence_score === 'high' ? 'success' : 'default'} />
              </Box>
              {response.headline && (
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{response.headline}</Typography>
              )}
              <Box sx={{ fontSize: '0.9rem', '& p': { mb: 1 }, '& h3, & h4': { mt: 1.5, mb: 0.5, fontWeight: 700 }, '& strong': { fontWeight: 700 } }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{response.text || ''}</ReactMarkdown>
              </Box>
              {response.follow_ups?.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {response.follow_ups.map((f, i) => (
                    <Chip key={i} label={f} size="small" variant="outlined" onClick={() => setMessage(f)} sx={{ cursor: 'pointer' }} />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        )}
        </div>
      </div>
    </Component>
  );
}

export default LanguageTest;
